'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

/**
 * Client component that updates the document title based on the current hostname
 * This allows the root layout to remain static while still showing deployment names
 */
export function DeploymentTitle() {
  const pathname = usePathname();
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hostname = window.location.hostname;
    let isUpdating = false;
    const titleElement = document.querySelector('title');

    // Detect deployment environment based on hostname
    const getDeploymentName = (hostname: string | null | undefined): string | null => {
      if (!hostname) {
        return null;
      }

      const hostnameLower = hostname.toLowerCase();

      // Check for dev deployment
      if (hostnameLower.includes('dev.alitahealth.ai')) {
        return 'dev';
      }

      // Check for stage deployment
      if (hostnameLower.includes('stage.alitahealth.ai')) {
        return 'stage';
      }

      // Check for sandbox deployment
      if (hostnameLower.includes('sandbox')) {
        return 'sandbox';
      }

      // Check for localhost
      if (hostnameLower.includes('localhost') || hostnameLower.includes('127.0.0.1')) {
        return 'local';
      }

      // Production or unknown - return null (no deployment name appended)
      return null;
    };

    // Function to update title with deployment name
    const updateTitle = () => {
      // Prevent recursive calls
      if (isUpdating) return;

      const currentTitle = document.title;
      const deployment = getDeploymentName(hostname);

      // Check if title already has the correct deployment name
      if (deployment) {
        const expectedSuffix = ` (${deployment})`;
        if (currentTitle.endsWith(expectedSuffix)) {
          return; // Already correct, don't update
        }
      } else {
        // For production, check if there's any deployment suffix
        if (!/\(dev|stage|sandbox|local\)$/i.test(currentTitle)) {
          return; // No deployment suffix, already correct
        }
      }

      // Remove any existing deployment names (handle corrupted titles with multiple parentheses)
      let baseTitle = currentTitle.replace(/\s*\(dev|stage|sandbox|local\)+$/i, '').trim();

      // Clean up any remaining malformed parentheses at the end
      baseTitle = baseTitle.replace(/\(+$/g, '').trim();

      // Fallback to default if empty
      if (!baseTitle) {
        baseTitle = 'Alita Hub';
      }

      // Get the page title with deployment name appended if not production
      const titleWithDeployment = deployment ? `${baseTitle} (${deployment})` : baseTitle;

      // Only update if different
      if (titleWithDeployment !== currentTitle) {
        isUpdating = true;

        // Disconnect observer temporarily to prevent loop
        if (observerRef.current) {
          observerRef.current.disconnect();
        }

        document.title = titleWithDeployment;

        // Reconnect observer after a brief delay
        setTimeout(() => {
          isUpdating = false;
          if (titleElement && observerRef.current) {
            observerRef.current.observe(titleElement, {
              childList: true,
              subtree: true,
              characterData: true,
            });
          }
        }, 50);
      }
    };

    // Update title immediately
    updateTitle();

    // Also update after a short delay to catch Next.js metadata updates
    const timeoutId = setTimeout(updateTitle, 100);

    // Watch for title changes using MutationObserver
    if (titleElement) {
      // Clean up existing observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new MutationObserver(() => {
        // Use requestAnimationFrame to avoid conflicts
        requestAnimationFrame(updateTitle);
      });

      observerRef.current.observe(titleElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      clearTimeout(timeoutId);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [pathname]);

  return null;
}
